import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { ProtectedRoute } from '@/auth/ProtectedRoute'
import { Layout } from '@/components/Layout'
import { Login } from '@/pages/Login'
import { Dashboard } from '@/pages/Dashboard'
import { Devices } from '@/pages/Devices'
import { DeviceDetail } from '@/pages/DeviceDetail'
import { Fleets } from '@/pages/Fleets'
import { Operations } from '@/pages/Operations'
import { ScheduledTasks } from '@/pages/ScheduledTasks'
import { Notifications } from '@/pages/Notifications'
import { Admin } from '@/pages/Admin'
import { OTA } from '@/pages/OTA'
import { Playbooks } from '@/pages/Playbooks'
import { Credentials } from '@/pages/Credentials'
import { TenantStatus } from '@/pages/TenantStatus'
import { Events } from '@/pages/Events'
import { DeviceTypes } from '@/pages/DeviceTypes'
import { SupportRequests } from '@/pages/SupportRequests'
import { Twins } from '@/pages/Twins'
import { Dashboards } from '@/pages/Dashboards'
import { useTenant } from '@/hooks/useTenant'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

function AppRoutes() {
  const auth = useAuth()
  const location = useLocation()
  const { activeTenantId, switchTenant } = useTenant()

  // Handle OIDC callback — the redirect_uri lands here
  if (auth.isLoading && location.pathname === '/callback') {
    return <div className="loading-screen">Completing sign-in…</div>
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Navigate to="/dashboard" replace />} />

      {/* All protected routes share the Layout */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout activeTenantId={activeTenantId} onTenantChange={switchTenant}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard tenantId={activeTenantId} />} />
                <Route
                  path="/devices"
                  element={
                    <ProtectedRoute require="devices:read">
                      <Devices tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/devices/:deviceId"
                  element={
                    <ProtectedRoute require="devices:read">
                      <DeviceDetail tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleets"
                  element={
                    <ProtectedRoute require="fleets:read">
                      <Fleets tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/operations"
                  element={
                    <ProtectedRoute require="operations:read">
                      <Operations tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/scheduled-tasks"
                  element={
                    <ProtectedRoute require="fleets:write">
                      <ScheduledTasks tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute require="admin:access">
                      <Notifications tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute require="admin:access">
                      <Admin tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ota"
                  element={
                    <ProtectedRoute require="fleets:write">
                      <OTA tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/playbooks"
                  element={
                    <ProtectedRoute require="fleets:write">
                      <Playbooks tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/credentials"
                  element={
                    <ProtectedRoute require="devices:write">
                      <Credentials tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device-types"
                  element={
                    <ProtectedRoute require="devices:read">
                      <DeviceTypes tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tenant-status"
                  element={
                    <ProtectedRoute require="devices:read">
                      <TenantStatus tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events"
                  element={
                    <ProtectedRoute require="devices:read">
                      <Events tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/twins"
                  element={
                    <ProtectedRoute require="devices:read">
                      <Twins tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboards"
                  element={
                    <ProtectedRoute require="devices:read">
                      <Dashboards tenantId={activeTenantId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/support-requests"
                  element={
                    <ProtectedRoute require="admin:access">
                      <SupportRequests />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppRoutes />
    </QueryClientProvider>
  )
}
